import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import AddDeviceGroupModal from 'content/modals/AddDeviceGroupModal';
import AddDeviceModal from 'content/modals/AddDeviceModal';
import AddDevicesToOrganizationModal from 'content/modals/AddDevicesToOrganizationModal';
import AddLoadBalancingGroupModal from 'content/modals/AddLoadBalancingGroupModal';
import AddMemberToOrganizationModal from 'content/modals/AddMemberToOrganizationModal';
import AddOrganizationModal from 'content/modals/AddOrganizationModal';
import AddRfidGroupModal from 'content/modals/AddRfidGroupModal';
import AddRfidModal from 'content/modals/AddRfidModal';
import AddRfidsToGroupsModal from 'content/modals/AddRfidsToGroupsModal';
import AssignDevicesToMembersModal from 'content/modals/AssignDevicesToMembersModal';
import AssignMembersToDevicesModal from 'content/modals/AssignMembersToDevicesModal';
import DeviceGroupConfigModal from 'content/modals/DeviceGroupConfigModal';
import GainFullDeviceAccessModal from 'content/modals/GainFullDeviceAccessModal';
import PairDevicesToRfidsModal from 'content/modals/PairDevicesToRfidsModal';
import ProfileModal from 'content/modals/ProfileModal';
import RebootDeviceModal from 'content/modals/RebootDeviceModal';
import RemoveDeviceGroupModal from 'content/modals/RemoveDeviceGroupModal';
import RemoveDeviceModal from 'content/modals/RemoveDeviceModal';
import RemoveMultipleDevicesModal from 'content/modals/RemoveMultipleDevicesModal';
import RemoveMultipleRfidsModal from 'content/modals/RemoveMultipleRfidsModal';
import RemoveOrganizationDeviceModal from 'content/modals/RemoveOrganizationDeviceModal';
import RemoveOrganizationMemberModal from 'content/modals/RemoveOrganizationMemberModal';
import RemoveOrganizationModal from 'content/modals/RemoveOrganizationModal';
import RemoveRfidGroupModal from 'content/modals/RemoveRfidGroupModal';
import RemoveRfidModal from 'content/modals/RemoveRfidModal';
import RenameDeviceGroupModal from 'content/modals/RenameDeviceGroupModal';
import RenameOrganizationModal from 'content/modals/RenameOrganizationModal';
import RenameRfidGroupModal from 'content/modals/RenameRfidGroupModal';
import TermsAndConditionsModal from 'content/modals/TermsAndConditionsModal';
import TroubleshootingDevicesModal from 'content/modals/TroubleshootingDevicesModal';
import UnassignDevicesFromMemberModal from 'content/modals/UnassignDevicesFromMemberModal';
import UnassignMembersFromDeviceModal from 'content/modals/UnassignMembersFromDeviceModal';
import UpdateRfidModal from 'content/modals/UpdateRfidModal';
import ReportIssueModal from 'content/pages/GeneralSettingsPage/GeneralSettingsTabs/GeneralTab/popups/ReportIssueModal';
import useIsAnonymousUser from 'hooks/useIsAnonymousUser';
import useProfile from 'hooks/useProfile';
import Cookies from 'js-cookie';
import { HtmlHead } from 'layouts/HtmlHead';
import type { SeoProps } from 'layouts/HtmlHead';
import Navbar from 'layouts/Navbar';
import Sidebar from 'layouts/Sidebar';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';

import pageUrls from 'constants/pageUrls';

import { useAppSelector, useAppDispatch } from 'redux-store';
import { toggleSidebar } from 'redux-store/slices/toggleSidebarMenu';
import {
  setAddDeviceGroupModal,
  setAddDeviceModal,
  setAddRfidGroupModal,
  setAddRfidModal,
  setRebootDeviceModal,
  setRemoveDeviceGroupModal,
  setRenameDeviceGroupModal,
  setRemoveDeviceModal,
  setRemoveMultipleDevicesModal,
  setUpdateRfidModal,
  setRemoveRfidModal,
  setRemoveRfidGroupModal,
  setRenameRfidGroupModal,
  setDeviceGroupConfigModal,
  setDeviceTroubleshootingModal,
  canGoBack,
  setRemoveMultipleRfidsModal,
  setAddOrganizationModal,
  setRenameOrganizationModal,
  setRemoveOrganizationModal,
  setAddMemberToOrganizationModal,
  setAssignMembersToDevicesModal,
  setAssignDevicesToMembersModal,
  setAddCurrentDevicesToOrganizationModal,
  setRemoveOrganizationDeviceModal,
  setRemoveOrganizationMemberModal,
  setUnassignMemebersFromDeviceModal,
  setAddRfidsToGroupsModal,
  setPairDevicesToRfidsModal,
  setGainFullDeviceAccessModal,
  setTermsAndConditionsModal,
  setUnassignDevicesFromMemberModal,
  setAddNewLoadBalancingGroup,
} from 'redux-store/slices/ui';
import type { DeviceSerial, RfidId } from 'redux-store/slices/ui/types';

import DebugFab from './DebugFab';

export interface DashboardWrapperProps {
  children: ReactNode;
  seo?: SeoProps;
}

interface DashboardContentProps {
  transitioning: boolean;
}

const DashboardContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'transitioning',
})<DashboardContentProps>(({ theme, transitioning }) => ({
  gridArea: 'content',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'auto',
  component: 'main',
  opacity: transitioning ? 0 : 1,
  transition: 'opacity 0.2s',
  backgroundColor: theme.vars.palette.common.white,
  ...theme.applyStyles('dark', {
    backgroundColor: theme.vars.palette.grey[900],
  }),
}));

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const ENABLE_HIDE_DURING_PAGE_TRANSITION =
  process.env.NEXT_PUBLIC_ENABLE_HIDE_DURING_PAGE_TRANSITION;

const DEFAULT_HIDE_FAB = BUILD_ENV !== 'development';
const DISABLE_HIDE_DURING_PAGE_TRANSITION =
  ENABLE_HIDE_DURING_PAGE_TRANSITION !== 'true';

// eslint-disable-next-line complexity
const DashboardWrapper: FC<DashboardWrapperProps> = ({ children, seo }) => {
  const theme = useTheme();
  const router = useRouter();
  const profile = useProfile();
  const isAnonymous = useIsAnonymousUser();
  const searchParams = useSearchParams();

  const isLicenseTab = searchParams.get('tab') === 'licenses';

  const dispatch = useAppDispatch();

  const [hideFab, setHideFab] = useState(DEFAULT_HIDE_FAB);

  const showDebugFab = process.env.BUILD_ENV !== 'production' && !hideFab;

  useEffect(() => {
    // when query param "debug" is present and set to true show debug modal
    if (router.query.debug === 'true') {
      setHideFab(false);
    }
  }, [router.query.debug]);

  useEffect(() => {
    const termsAcceptedCookie = Cookies.get('termsAccepted');
    const shouldShowModal =
      (isAnonymous && termsAcceptedCookie !== 'true') ||
      (!isAnonymous && profile?.termsAcceptedAt === null);

    if (shouldShowModal) {
      dispatch(setTermsAndConditionsModal(true));
    }
  }, [isAnonymous, profile?.termsAcceptedAt, dispatch]);

  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));

  const [transitioning, setTransitioning] = useState(false);

  const showAddDeviceModal = useAppSelector(state => state.ui.addDeviceModal);
  const removeDeviceState = useAppSelector(state => state.ui.removeDeviceModal);
  const rebootDeviceState = useAppSelector(state => state.ui.rebootDeviceModal);
  const removeMultipleDevicesState = useAppSelector(
    state => state.ui.removeMultipleDevicesModal,
  );
  const removeDeviceGroupState = useAppSelector(
    state => state.ui.deviceGroups.removeDeviceGroupModal,
  );
  const renameDeviceGroupState = useAppSelector(
    state => state.ui.deviceGroups.renameDeviceGroupModal,
  );
  const showAddDeviceGroupModal = useAppSelector(
    state => state.ui.deviceGroups.addDeviceGroupModal,
  );

  const showDeviceGroupConfigModal = useAppSelector(
    state => state.ui.deviceGroupConfigModal,
  );

  const showDeviceTroubleshootingModal = useAppSelector(
    state => state.ui.deviceTroubleshootingModal,
  );

  const showAddRfidModal = useAppSelector(state => state.ui.addRfidModal);
  const showAddRfidGroupModal = useAppSelector(
    state => state.ui.rfidGroups.addRfidGroupModal,
  );
  const showUpdateRfidModal = useAppSelector(state => state.ui.updateRfidModal);

  const removeRfidState = useAppSelector(state => state.ui.removeRfidModal);

  const removeMultipleRfidsState = useAppSelector(
    state => state.ui.removeMultipleRfidsModal,
  );

  const removeRfidGroupState = useAppSelector(
    state => state.ui.rfidGroups.removeRfidGroupModal,
  );
  const renameRfidGroupState = useAppSelector(
    state => state.ui.rfidGroups.renameRfidGroupModal,
  );

  const showAddRfidsToGroupsState = useAppSelector(
    state => state.ui.showAddRfidsToGroupsModal,
  );

  const showGainFullDeviceAccessModal = useAppSelector(
    state => state.ui.gainFullDeviceAccessModal,
  );

  const showAddOrganizationModal = useAppSelector(
    state => state.ui.organizations.addOrganizationModal,
  );
  const removeOrganizationState = useAppSelector(
    state => state.ui.organizations.removeOrganizationModal,
  );
  const renameOrganizationState = useAppSelector(
    state => state.ui.organizations.renameOrganizationModal,
  );

  const showAddMembersToOrganizationState = useAppSelector(
    state => state.ui.showAddMemberToOrganizationModal,
  );
  const showAddCurrentDevicesToOrganizationState = useAppSelector(
    state => state.ui.showAddCurrentDevicesToOrganizationModal,
  );
  const showAssignMembersToDeviceState = useAppSelector(
    state => state.ui.showAssignMembersToDevicesModal,
  );

  const showAssignDevicesToMembersState = useAppSelector(
    state => state.ui.showAssignDevicesToMembersModal,
  );

  const removeOrganizationDeviceState = useAppSelector(
    state => state.ui.removeOrganizationDeviceModal,
  );
  const removeOrganizationMemberState = useAppSelector(
    state => state.ui.removeOrganizationMemberModal,
  );

  const unassignMembersFromDeviceState = useAppSelector(
    state => state.ui.unassignMembersFromDeviceModal,
  );

  const unassignDevicesFromMemberState = useAppSelector(
    state => state.ui.unassignDevicesFromMemberModal,
  );

  const pairDevicesFromRfidsState = useAppSelector(
    state => state.ui.pairDevicesToRfidsModal,
  );

  const termsAndConditionsModalState = useAppSelector(
    state => state.ui.termsAndConditionsModal,
  );

  const addLoadBalancingGroupModalState = useAppSelector(
    state => state.ui.addNewLoadBalancingGroup,
  );

  const closeAddDeviceModal = useCallback(() => {
    dispatch(setAddDeviceModal({ open: false, groupId: null }));
  }, [dispatch]);

  const closeRemoveDeviceModal = useCallback(() => {
    dispatch(setRemoveDeviceModal(null));
  }, [dispatch]);

  const closeRemoveMultipleDevicesModal = useCallback(() => {
    dispatch(setRemoveMultipleDevicesModal(null));
  }, [dispatch]);

  const closeRemoveDeviceGroupModal = useCallback(() => {
    dispatch(setRemoveDeviceGroupModal(null));
  }, [dispatch]);

  const closeRenameDeviceGroupModal = useCallback(() => {
    dispatch(setRenameDeviceGroupModal(null));
  }, [dispatch]);

  const closeRebootDeviceModal = useCallback(() => {
    dispatch(setRebootDeviceModal(null));
  }, [dispatch]);

  const handleHideAddDeviceGroupModal = useCallback(() => {
    dispatch(setAddDeviceGroupModal({ serial: false, groupID: null }));
  }, [dispatch]);

  const closeDeviceGroupConfigModal = useCallback(() => {
    dispatch(
      setDeviceGroupConfigModal({
        open: false,
        deviceSerialNumbers: null,
        isSingleDeviceConfig: false,
      }),
    );
  }, [dispatch]);

  const closeDeviceTroubleshootingModal = useCallback(() => {
    dispatch(
      setDeviceTroubleshootingModal({
        open: false,
        deviceSerialNumber: null,
        status: null,
      }),
    );
  }, [dispatch]);

  const closeAddRfidModal = useCallback(() => {
    dispatch(setAddRfidModal({ open: false, groupId: null }));
  }, [dispatch]);

  const closeUpdateRfidModal = useCallback(() => {
    dispatch(setUpdateRfidModal(0));
  }, [dispatch]);

  const closeRemoveRfidModal = useCallback(() => {
    dispatch(setRemoveRfidModal(null));
  }, [dispatch]);

  const closeRemoveMultipleRfidsModal = useCallback(() => {
    dispatch(setRemoveMultipleRfidsModal(null));
  }, [dispatch]);

  const handleHideAddRfidGroupModal = useCallback(() => {
    dispatch(setAddRfidGroupModal({ rfid: false, groupID: null }));
  }, [dispatch]);

  const closeRemoveRfidGroupModal = useCallback(() => {
    dispatch(setRemoveRfidGroupModal(null));
  }, [dispatch]);

  const closeRenameRfidGroupModal = useCallback(() => {
    dispatch(setRenameRfidGroupModal(null));
  }, [dispatch]);

  const closeAddRfidsToGroupsModal = useCallback(() => {
    dispatch(setAddRfidsToGroupsModal(null));
  }, [dispatch]);

  const closeGainFullDeviceAccessModal = useCallback(() => {
    dispatch(setGainFullDeviceAccessModal({ open: false, serial: '' }));
  }, [dispatch]);

  const closeAddOrganizationModal = useCallback(() => {
    dispatch(setAddOrganizationModal(false));
  }, [dispatch]);

  const closeRemoveOrganizationModal = useCallback(() => {
    dispatch(setRemoveOrganizationModal(null));
  }, [dispatch]);

  const closeRenameOrganizationModal = useCallback(() => {
    dispatch(setRenameOrganizationModal(null));
  }, [dispatch]);

  const closeAddMembersModal = useCallback(() => {
    dispatch(
      setAddMemberToOrganizationModal({
        isOpen: false,
        organizationId: null,
      }),
    );
  }, [dispatch]);

  const closeAddDevicesModal = useCallback(() => {
    dispatch(
      setAddCurrentDevicesToOrganizationModal({
        isOpen: false,
        organizationId: null,
        selectedDevices: [],
      }),
    );
  }, [dispatch]);

  const closeAssignMembersModal = useCallback(() => {
    dispatch(
      setAssignMembersToDevicesModal({
        isOpen: false,
        organizationId: null,
        deviceId: null,
        hasAssignedMembers: false,
      }),
    );
  }, [dispatch]);

  const closeAssignDevicesModal = useCallback(
    () =>
      dispatch(
        setAssignDevicesToMembersModal({
          isOpen: false,
          organizationId: null,
          userId: null,
          hasAssignedDevices: false,
        }),
      ),
    [dispatch],
  );

  const closeRemoveOrganizationDeviceModal = useCallback(() => {
    dispatch(
      setRemoveOrganizationDeviceModal({
        isOpen: false,
        organizationId: null,
        deviceIds: [],
      }),
    );
  }, [dispatch]);

  const closeRemoveOrganizationMemberModal = useCallback(() => {
    dispatch(
      setRemoveOrganizationMemberModal({
        isOpen: false,
        organizationId: null,
        memberIds: [],
      }),
    );
  }, [dispatch]);

  const closeUnassignMembersFromDeviceModal = useCallback(() => {
    dispatch(
      setUnassignMemebersFromDeviceModal({
        isOpen: false,
        organizationId: null,
        deviceId: null,
        memberIds: [],
      }),
    );
  }, [dispatch]);

  const closeUnassignDevicesFromMemberModal = useCallback(() => {
    dispatch(
      setUnassignDevicesFromMemberModal({
        isOpen: false,
        organizationId: null,
        memberId: null,
        devicesIds: [],
      }),
    );
  }, [dispatch]);

  const closePairDevicesToRfidsModal = useCallback(() => {
    dispatch(
      setPairDevicesToRfidsModal({
        isOpen: false,
        selectedRfids: [],
        pairedDevices: false,
      }),
    );
  }, [dispatch]);

  const closeTermsAndConditionsModal = useCallback(() => {
    dispatch(setTermsAndConditionsModal(false));
  }, [dispatch]);

  const closeAddLoadBalancingGroupModal = useCallback(() => {
    dispatch(
      setAddNewLoadBalancingGroup({
        isOpen: false,
        selectedDevices: [],
        selectedGroup: null,
        configureGroup: false,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const handleRouteDone = (): void => {
      dispatch(canGoBack());
    };

    router.events.on('routeChangeComplete', handleRouteDone);

    return () => {
      router.events.off('routeChangeComplete', handleRouteDone);
    };
  }, [dispatch, router.events]);

  useEffect(() => {
    if (DISABLE_HIDE_DURING_PAGE_TRANSITION) {
      return;
    }

    const handleRouteDone = (): void => {
      setTransitioning(false);
    };

    const handleRouteStart = (): void => {
      setTransitioning(true);
    };

    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', handleRouteDone);
    router.events.on('routeChangeError', handleRouteDone);

    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', handleRouteDone);
      router.events.off('routeChangeError', handleRouteDone);
    };
  }, [router.events]);

  const isSidebarOpen = useAppSelector(state => state.sidebar.isOpen);

  const handleToggleSidebar = (): void => {
    dispatch(toggleSidebar());
  };

  return (
    <>
      <HtmlHead {...seo} />
      <Box display="flex" alignItems="flex-start" width="100%" height="100%">
        {router.pathname !== pageUrls.setupDevices ? <Sidebar /> : null}
        <DashboardContent
          transitioning={transitioning}
          data-testid="dashboard-wrapper"
        >
          {lgDown ? (
            <Navbar
              handleToggleSidebar={handleToggleSidebar}
              isSidebarOpen={isSidebarOpen}
            />
          ) : null}
          <Box
            p={lgDown ? 3 : 5}
            pt={3}
            style={isLicenseTab ? { height: '100%', paddingBottom: 0 } : {}}
          >
            {children}
          </Box>
        </DashboardContent>
      </Box>
      {showDebugFab ? <DebugFab setHideFab={setHideFab} /> : null}
      <AddDeviceModal
        open={showAddDeviceModal.open}
        groupId={showAddDeviceModal.groupId ?? undefined}
        onClose={closeAddDeviceModal}
      />
      <RemoveDeviceModal
        open={!!removeDeviceState}
        onClose={closeRemoveDeviceModal}
        serial={removeDeviceState}
      />
      <RemoveMultipleDevicesModal
        open={!!removeMultipleDevicesState}
        onClose={closeRemoveMultipleDevicesModal}
        serials={removeMultipleDevicesState}
      />
      <RemoveDeviceGroupModal
        open={!!removeDeviceGroupState}
        onClose={closeRemoveDeviceGroupModal}
        groupId={removeDeviceGroupState}
      />
      <RenameDeviceGroupModal
        open={!!renameDeviceGroupState}
        onClose={closeRenameDeviceGroupModal}
        groupId={renameDeviceGroupState}
      />
      <RebootDeviceModal
        open={!!rebootDeviceState}
        onClose={closeRebootDeviceModal}
        serial={rebootDeviceState}
      />
      <AddDeviceGroupModal
        open={showAddDeviceGroupModal.serial !== false}
        preselectedDevices={showAddDeviceGroupModal.serial as DeviceSerial[]}
        groupID={showAddDeviceGroupModal.groupID ?? null}
        onClose={handleHideAddDeviceGroupModal}
      />
      <DeviceGroupConfigModal
        open={showDeviceGroupConfigModal.open}
        deviceSerialNumbers={
          showDeviceGroupConfigModal.deviceSerialNumbers ?? undefined
        }
        isSingleDeviceConfig={showDeviceGroupConfigModal.isSingleDeviceConfig}
        onClose={closeDeviceGroupConfigModal}
      />
      <TroubleshootingDevicesModal
        open={showDeviceTroubleshootingModal.open}
        deviceSerialNumber={
          showDeviceTroubleshootingModal.deviceSerialNumber ?? undefined
        }
        status={showDeviceTroubleshootingModal.status}
        onClose={closeDeviceTroubleshootingModal}
      />
      <AddRfidModal
        open={showAddRfidModal.open}
        groupId={showAddRfidModal.groupId ?? undefined}
        onClose={closeAddRfidModal}
      />
      <UpdateRfidModal
        open={!!showUpdateRfidModal}
        rfidId={showUpdateRfidModal}
        onClose={closeUpdateRfidModal}
      />
      <RemoveRfidModal
        open={!!removeRfidState}
        onClose={closeRemoveRfidModal}
        rfidId={removeRfidState}
      />
      <RemoveMultipleRfidsModal
        open={!!removeMultipleRfidsState}
        onClose={closeRemoveMultipleRfidsModal}
        rfidIds={removeMultipleRfidsState}
      />
      <AddRfidGroupModal
        open={showAddRfidGroupModal.rfid !== false}
        preselectedRfids={showAddRfidGroupModal.rfid as RfidId[]}
        groupID={showAddRfidGroupModal.groupID ?? null}
        onClose={handleHideAddRfidGroupModal}
      />
      <RemoveRfidGroupModal
        open={!!removeRfidGroupState}
        onClose={closeRemoveRfidGroupModal}
        groupId={removeRfidGroupState}
      />
      <RenameRfidGroupModal
        open={!!renameRfidGroupState}
        onClose={closeRenameRfidGroupModal}
        groupId={renameRfidGroupState}
      />
      <AddRfidsToGroupsModal
        open={!!showAddRfidsToGroupsState}
        onClose={closeAddRfidsToGroupsModal}
        selectedRfids={showAddRfidsToGroupsState ?? []}
      />
      <GainFullDeviceAccessModal
        open={showGainFullDeviceAccessModal.open}
        serial={showGainFullDeviceAccessModal.serial}
        onClose={closeGainFullDeviceAccessModal}
      />
      <AddOrganizationModal
        open={showAddOrganizationModal !== false}
        onClose={closeAddOrganizationModal}
      />
      <RemoveOrganizationModal
        open={!!removeOrganizationState}
        onClose={closeRemoveOrganizationModal}
        organizationId={removeOrganizationState}
      />
      <RenameOrganizationModal
        open={!!renameOrganizationState}
        onClose={closeRenameOrganizationModal}
        organizationId={renameOrganizationState}
      />
      <AddMemberToOrganizationModal
        open={showAddMembersToOrganizationState.isOpen}
        organizationId={showAddMembersToOrganizationState.organizationId}
        onClose={closeAddMembersModal}
        selectedDevices={
          showAddMembersToOrganizationState.selectedDevices ?? []
        }
      />
      <AddDevicesToOrganizationModal
        open={showAddCurrentDevicesToOrganizationState.isOpen}
        organizationId={showAddCurrentDevicesToOrganizationState.organizationId}
        onClose={closeAddDevicesModal}
        selectedDevices={
          showAddCurrentDevicesToOrganizationState.selectedDevices
        }
      />
      <AssignMembersToDevicesModal
        open={showAssignMembersToDeviceState.isOpen}
        onClose={closeAssignMembersModal}
        organizationId={showAssignMembersToDeviceState.organizationId ?? null}
        deviceId={showAssignMembersToDeviceState.deviceId ?? null}
        selectedDevices={showAssignMembersToDeviceState.selectedDevices ?? []}
        hasAssignedMembers={showAssignMembersToDeviceState.hasAssignedMembers}
      />
      <AssignDevicesToMembersModal
        open={showAssignDevicesToMembersState.isOpen}
        onClose={closeAssignDevicesModal}
        organizationId={showAssignDevicesToMembersState.organizationId ?? null}
        userId={showAssignDevicesToMembersState.userId ?? null}
        hasAssignedDevices={showAssignDevicesToMembersState.hasAssignedDevices}
        selectedMembers={showAssignDevicesToMembersState.selectedMembers ?? []}
      />
      <RemoveOrganizationDeviceModal
        open={removeOrganizationDeviceState.isOpen}
        onClose={closeRemoveOrganizationDeviceModal}
        organizationId={removeOrganizationDeviceState.organizationId}
        deviceIds={removeOrganizationDeviceState.deviceIds}
        removeMultipleDevices={
          removeOrganizationDeviceState.removeMultipleDevices ?? false
        }
      />
      <RemoveOrganizationMemberModal
        open={removeOrganizationMemberState.isOpen}
        onClose={closeRemoveOrganizationMemberModal}
        organizationId={removeOrganizationMemberState.organizationId}
        memberIds={removeOrganizationMemberState.memberIds}
        removeMultipleMembers={
          removeOrganizationMemberState.removeMultipleMembers ?? false
        }
      />
      <UnassignMembersFromDeviceModal
        open={unassignMembersFromDeviceState.isOpen}
        organizationId={unassignMembersFromDeviceState.organizationId}
        deviceId={unassignMembersFromDeviceState.deviceId ?? null}
        memberIds={unassignMembersFromDeviceState.memberIds}
        onClose={closeUnassignMembersFromDeviceModal}
      />
      <UnassignDevicesFromMemberModal
        open={unassignDevicesFromMemberState.isOpen}
        organizationId={unassignDevicesFromMemberState.organizationId}
        memberId={unassignDevicesFromMemberState.memberId ?? null}
        deviceIds={unassignDevicesFromMemberState.devicesIds ?? []}
        onClose={closeUnassignDevicesFromMemberModal}
      />
      <PairDevicesToRfidsModal
        open={pairDevicesFromRfidsState.isOpen}
        pairedDevices={pairDevicesFromRfidsState.pairedDevices}
        selectedRfids={pairDevicesFromRfidsState.selectedRfids}
        onClose={closePairDevicesToRfidsModal}
      />
      <TermsAndConditionsModal
        open={termsAndConditionsModalState}
        onClose={closeTermsAndConditionsModal}
      />
      <AddLoadBalancingGroupModal
        open={addLoadBalancingGroupModalState.isOpen}
        onClose={closeAddLoadBalancingGroupModal}
        {...addLoadBalancingGroupModalState}
      />
      <ProfileModal />
      <ReportIssueModal />
    </>
  );
};

export default DashboardWrapper;
